import React, { useMemo, useState } from 'react';
import { Box, Stack, TextField, Typography } from '@mui/material';
import { PamGrid } from '@1823-partners/core';
import { formatCell, isDateCol, isNumCol, toDateObj } from '../../utils/derivativeReconFormat';

export default function RawTable({ rows }) {
  const [search, setSearch] = useState('');

  const cols = useMemo(() => (rows && rows.length ? Object.keys(rows[0]) : []), [rows]);

  const columnDefs = useMemo(
    () =>
      cols.map((c) => ({
        field: c,
        headerName: c,
        minWidth: 130,
        valueFormatter: (p) => formatCell(c, p.value),
        comparator: (a, b) => {
          if (isDateCol(c)) {
            const da = toDateObj(a)?.getTime() ?? -1;
            const db = toDateObj(b)?.getTime() ?? -1;
            return da - db;
          }
          if (isNumCol(c)) {
            const an = parseFloat(String(a || '').replace(/,/g, ''));
            const bn = parseFloat(String(b || '').replace(/,/g, ''));
            return (isNaN(an) ? 0 : an) - (isNaN(bn) ? 0 : bn);
          }
          return String(a || '').localeCompare(String(b || ''));
        },
      })),
    [cols],
  );

  const filtered = useMemo(() => {
    if (!rows || !rows.length) return [];
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter((row) =>
      cols.some((c) => String(row[c] ?? '').toLowerCase().includes(s)),
    );
  }, [rows, cols, search]);

  if (!rows || !rows.length) {
    return (
      <Typography sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
        No data available.
      </Typography>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} mb={1.5} alignItems="center">
        <TextField
          size="small"
          placeholder="Search any field…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1 }}
        />
        <Typography variant="caption" color="text.secondary">
          {filtered.length} of {rows.length} rows · {cols.length} cols
        </Typography>
      </Stack>
      <Box sx={{ height: 640, width: '100%' }}>
        <PamGrid
          rowData={filtered}
          columnDefs={columnDefs}
          defaultColDef={{ sortable: true, resizable: true, filter: true, wrapHeaderText: true, autoHeaderHeight: true }}
          enableShowToolbar={false}
        />
      </Box>
    </Box>
  );
}
